import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import './LeadDetailModal.css';
import { Modal, Button, TextInput, Textarea, Alert } from '../../common';
import { leadApi } from '../../../services/apis/configuredApis';

function LeadDetailModal({ show, lead, mode = 'view', onClose, onEdit, onSuccess }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_name: '',
      job_title: '',
      country: '',
      region_state: '',
      sales_notes: '',
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
    },
  });

  useEffect(() => {
    if (!show) {
      setError(null);
      return;
    }
    
    if (lead) {
      reset({
        first_name: lead.first_name || '',
        last_name: lead.last_name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        company_name: lead.company_name || '',
        job_title: lead.job_title || '',
        country: lead.country || '',
        region_state: lead.region_state || '',
        sales_notes: lead.sales_notes || '',
        utm_source: lead.utm_source || '',
        utm_medium: lead.utm_medium || '',
        utm_campaign: lead.utm_campaign || '',
      });
    }
    setError(null);
  }, [show, lead, reset]);

  const onSubmit = async (data) => {
    if (mode !== 'edit') return;
    
    try {
      setLoading(true);
      setError(null);
      
      await leadApi.leadUpdateLeadIdPut({
        leadId: lead.id,
        leadUpdate: data,
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error updating lead:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to update lead';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const isEditMode = mode === 'edit';

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      title={isEditMode ? 'Edit Lead' : 'Lead Details'}
      size="large"
      footer={
        isEditMode ? (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
            >
              Close
            </Button>
            {onEdit && (
              <Button
                variant="primary"
                onClick={onEdit}
              >
                Edit
              </Button>
            )}
          </>
        )
      }
    >
      <div className="lead-detail-modal">
        {error && (
          <Alert
            variant="error"
            onClose={() => setError(null)}
            className="lead-detail-modal__alert"
          >
            {error}
          </Alert>
        )}

        {isEditMode ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="lead-detail-modal__section">
              <h3 className="lead-detail-modal__section-title">Contact Information</h3>
              <div className="lead-detail-modal__fields">
                <TextInput
                  label="First Name"
                  {...register('first_name')}
                  error={errors.first_name?.message}
                  disabled={loading}
                />
                <TextInput
                  label="Last Name"
                  {...register('last_name')}
                  error={errors.last_name?.message}
                  disabled={loading}
                />
                <TextInput
                  label="Email"
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={errors.email?.message}
                  disabled={loading}
                />
                <TextInput
                  label="Phone"
                  {...register('phone')}
                  error={errors.phone?.message}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="lead-detail-modal__section">
              <h3 className="lead-detail-modal__section-title">Company Information</h3>
              <div className="lead-detail-modal__fields">
                <TextInput
                  label="Company Name"
                  {...register('company_name')}
                  error={errors.company_name?.message}
                  disabled={loading}
                />
                <TextInput
                  label="Job Title"
                  {...register('job_title')}
                  error={errors.job_title?.message}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="lead-detail-modal__section">
              <h3 className="lead-detail-modal__section-title">Location</h3>
              <div className="lead-detail-modal__fields">
                <TextInput
                  label="Country"
                  {...register('country')}
                  error={errors.country?.message}
                  disabled={loading}
                />
                <TextInput
                  label="Region/State"
                  {...register('region_state')}
                  error={errors.region_state?.message}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="lead-detail-modal__section">
              <h3 className="lead-detail-modal__section-title">Sales & Marketing</h3>
              <div className="lead-detail-modal__fields">
                <Textarea
                  label="Sales Notes"
                  {...register('sales_notes')}
                  error={errors.sales_notes?.message}
                  disabled={loading}
                  rows={4}
                />
                <TextInput
                  label="UTM Source"
                  {...register('utm_source')}
                  error={errors.utm_source?.message}
                  disabled={loading}
                />
                <TextInput
                  label="UTM Medium"
                  {...register('utm_medium')}
                  error={errors.utm_medium?.message}
                  disabled={loading}
                />
                <TextInput
                  label="UTM Campaign"
                  {...register('utm_campaign')}
                  error={errors.utm_campaign?.message}
                  disabled={loading}
                />
              </div>
            </div>
          </form>
        ) : (
          <div className="lead-detail-modal__view">
            <div className="lead-detail-modal__section">
              <h3 className="lead-detail-modal__section-title">Contact Information</h3>
              <div className="lead-detail-modal__info-grid">
                <div className="lead-detail-modal__info-item">
                  <label>First Name</label>
                  <span>{lead?.first_name || '—'}</span>
                </div>
                <div className="lead-detail-modal__info-item">
                  <label>Last Name</label>
                  <span>{lead?.last_name || '—'}</span>
                </div>
                <div className="lead-detail-modal__info-item">
                  <label>Email</label>
                  <span>
                    {lead?.email ? (
                      <a href={`mailto:${lead.email}`}>{lead.email}</a>
                    ) : '—'}
                  </span>
                </div>
                <div className="lead-detail-modal__info-item">
                  <label>Phone</label>
                  <span>{lead?.phone || '—'}</span>
                </div>
              </div>
            </div>

            <div className="lead-detail-modal__section">
              <h3 className="lead-detail-modal__section-title">Company Information</h3>
              <div className="lead-detail-modal__info-grid">
                <div className="lead-detail-modal__info-item">
                  <label>Company Name</label>
                  <span>{lead?.company_name || '—'}</span>
                </div>
                <div className="lead-detail-modal__info-item">
                  <label>Job Title</label>
                  <span>{lead?.job_title || '—'}</span>
                </div>
              </div>
            </div>

            <div className="lead-detail-modal__section">
              <h3 className="lead-detail-modal__section-title">Location</h3>
              <div className="lead-detail-modal__info-grid">
                <div className="lead-detail-modal__info-item">
                  <label>Country</label>
                  <span>{lead?.country || '—'}</span>
                </div>
                <div className="lead-detail-modal__info-item">
                  <label>Region/State</label>
                  <span>{lead?.region_state || '—'}</span>
                </div>
              </div>
            </div>

            <div className="lead-detail-modal__section">
              <h3 className="lead-detail-modal__section-title">Activity</h3>
              <div className="lead-detail-modal__info-grid">
                <div className="lead-detail-modal__info-item">
                  <label>Consent Given</label>
                  <span>{lead?.consent_given ? 'Yes' : 'No'}</span>
                </div>
                <div className="lead-detail-modal__info-item">
                  <label>Download Count</label>
                  <span>{lead?.download_count || 0}</span>
                </div>
                <div className="lead-detail-modal__info-item">
                  <label>Created At</label>
                  <span>{formatDate(lead?.created_at)}</span>
                </div>
                <div className="lead-detail-modal__info-item">
                  <label>Last Download</label>
                  <span>{formatDate(lead?.last_download_at)}</span>
                </div>
              </div>
            </div>

            {(lead?.sales_notes || lead?.utm_source || lead?.utm_medium || lead?.utm_campaign) && (
              <div className="lead-detail-modal__section">
                <h3 className="lead-detail-modal__section-title">Sales & Marketing</h3>
                <div className="lead-detail-modal__info-grid">
                  {lead?.sales_notes && (
                    <div className="lead-detail-modal__info-item lead-detail-modal__info-item--full">
                      <label>Sales Notes</label>
                      <span>{lead.sales_notes}</span>
                    </div>
                  )}
                  {lead?.utm_source && (
                    <div className="lead-detail-modal__info-item">
                      <label>UTM Source</label>
                      <span>{lead.utm_source}</span>
                    </div>
                  )}
                  {lead?.utm_medium && (
                    <div className="lead-detail-modal__info-item">
                      <label>UTM Medium</label>
                      <span>{lead.utm_medium}</span>
                    </div>
                  )}
                  {lead?.utm_campaign && (
                    <div className="lead-detail-modal__info-item">
                      <label>UTM Campaign</label>
                      <span>{lead.utm_campaign}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default LeadDetailModal;











